import {
    Enrichment,
    TableColumns,
    Table,
    Cleanroom,
} from "../../common/types/cleanroom";
import { DatasourceType } from "../../common/types/config";
import { parseFQTNC } from "../../../common/utils/utils";
import { Tab } from "../CleanroomList/CleanroomListSlice";
import {
    APPLICATION_IDS,
    CLEANROOM_SIDE,
    THIRD_PARTY_IDENTITY_PROVIDER,
} from "../../common/constants/constants";
import {
    Activation,
    ApplicationId,
    CleanroomCreateInstallThunkActions,
    isHasCollaborator,
    Options,
    TableColumSelector,
} from "./cleanroomCreateInstall.types";
import { LIVERAMP_IDENTITY_SOLUTION_TYPE } from "./Step2/constants";
import { generateColumnsForLiveRamp } from "./Step2/enrichment.utils";
import { IdHubOptionsWithTag } from "./Step2/IdHubOptionsWithTag";
import { ApplicationTable } from "./Step3/ApplicationInitialState";
import cloneDeep from "lodash/cloneDeep";
import { DraftCleanroom } from "../../services/cleanrooms/CleanroomRequestResponse.types";
import { StatusState } from "../../store/slices/statusSlice";

export const getButtonConfig = ({
    activeStep,
    isProviderRun,
    status,
}: {
    activeStep: number;
    isProviderRun: boolean;
    status?: StatusState;
}) => {
    const commonButtons = [
        {
            disabled: false,
            variant: "outlined",
            text: "Cancel",
            color: "info",
            align: "left",
        },
    ];

    const stepConfigs = {
        0: [
            ...commonButtons,
            {
                variant: "contained",
                text: "Next",
                color: "primary",
                align: "right",
            },
        ],
        1: [
            ...commonButtons,
            {
                disabled: false,
                variant: "outlined",
                text: "Back",
                color: "primary",
                align: "right",
            },
            {
                disabled: false,
                variant: "contained",
                text: "Next",
                color: "primary",
                align: "right",
            },
        ],
        2: [
            ...commonButtons,
            {
                disabled: false,
                variant: "outlined",
                text: "Back",
                color: "primary",
                align: "right",
            },
            {
                variant: "contained",
                text: isProviderRun ? "Next" : "Finish",
                isLoading: !isProviderRun
                    ? status[
                          CleanroomCreateInstallThunkActions.HANDLE_FINISH_BTN
                      ] === "LOADING"
                    : false,
                disabled: !isProviderRun
                    ? status[
                          CleanroomCreateInstallThunkActions.HANDLE_FINISH_BTN
                      ] === "LOADING" ||
                      status[
                          CleanroomCreateInstallThunkActions.HANDLE_FINISH_BTN
                      ] === "SUCCESS"
                    : false,
                color: "primary",
                align: "right",
            },
        ],
        3: [
            ...commonButtons,
            {
                disabled: false,
                variant: "outlined",
                text: "Back",
                color: "primary",
                align: "right",
            },
            {
                variant: "contained",
                text: "Finish",
                color: "primary",
                align: "right",
                isLoading:
                    status[
                        CleanroomCreateInstallThunkActions.HANDLE_FINISH_BTN
                    ] === "LOADING",
                disabled:
                    status[
                        CleanroomCreateInstallThunkActions.HANDLE_FINISH_BTN
                    ] === "LOADING" ||
                    status[
                        CleanroomCreateInstallThunkActions.HANDLE_FINISH_BTN
                    ] === "SUCCESS",
            },
        ],
    };

    return stepConfigs[activeStep] || [];
};

export const createPreviewTableData = (columns: TableColumns[]) => {
    const columnNames =
        columns?.map((column) => parseFQTNC(column.fqtc).pop()) || [];

    const rowsData = Array(5)
        .fill(null)
        .map(() => {
            const row: { [key: string]: string } = {};
            columnNames?.forEach((columnName: string) => {
                row[columnName] = "*****";
            });
            return row;
        });

    return {
        column_data: columnNames || [],
        rows_data: rowsData || [],
    };
};

export const processAvailableObjects = (
    availableObjects: TableColumSelector[],
    selectedDatasourceId: DatasourceType,
    disabledTables: string[],
    invalidObjects: string[],
    localBootstrappedDb: string,
): Options[] => {
    const prefixes = {
        azure: `${localBootstrappedDb}.PUBLIC.AZURE_`,
        s3: `${localBootstrappedDb}.PUBLIC.S3_`,
        gcs: `${localBootstrappedDb}.PUBLIC.GCS_`,
    };

    type Categorized = {
        azure: string[];
        s3: string[];
        gcs: string[];
        snowflake: string[];
    };

    const categorizedInvalidObjects = (
        invalidObjects ?? []
    ).reduce<Categorized>(
        (acc, table) => {
            if (table.startsWith(prefixes.azure)) acc.azure.push(table);
            else if (table.startsWith(prefixes.s3)) acc.s3.push(table);
            else if (table.startsWith(prefixes.gcs)) acc.gcs.push(table);
            else acc.snowflake.push(table);
            return acc;
        },
        { azure: [], s3: [], gcs: [], snowflake: [] },
    );

    const invalidSet = new Set(invalidObjects ?? []);
    const disabledSet = new Set(disabledTables ?? []);

    const availableTables = availableObjects.filter(
        (table) =>
            table.source === selectedDatasourceId &&
            !invalidSet.has(table.fqtn),
    );

    const isAzure = selectedDatasourceId === "AZURE_CONNECTOR_ID";
    const isAws = selectedDatasourceId === "AWS_CONNECTOR_ID";
    const isGcp = selectedDatasourceId === "GCP_CONNECTOR_ID";

    const invalidsToAppend = isAzure
        ? categorizedInvalidObjects.azure
        : isAws
          ? categorizedInvalidObjects.s3
          : isGcp
            ? categorizedInvalidObjects.gcs
            : categorizedInvalidObjects.snowflake;

    const appendedType = isAzure || isAws || isGcp ? "EXTERNAL_TABLE" : "TABLE";

    if (invalidsToAppend.length > 0) {
        invalidsToAppend.forEach((table) => {
            availableTables.push({
                fqtn: table,
                source: selectedDatasourceId,
                object_type: appendedType,
            });
        });
    }

    const useUriLabel = isAws || isGcp || isAzure;
    const tablesWithInvalidObjects =
        availableTables.map((table) => {
            const parsed = parseFQTNC(table.fqtn);
            return {
                label: useUriLabel
                    ? table.object_uri || table.fqtn
                    : parsed.slice(1).join("."),
                value: table.fqtn,
                category: parsed.slice(0, 1).join("."),
                isDisabled: disabledSet.has(table.fqtn),
                isUnavailable: invalidSet.has(table.fqtn),
            };
        }) || [];
    return tablesWithInvalidObjects;
};

/**
 * Updates the nested object based on the provided path and value.
 * If the value is `null`, the property is removed. If the path does not exist, it will be created.
 *
 * @param {Object} obj - The object to update.
 * @param {string} path - The path to the property to update, using dot notation.
 * @param {*} value - The new value for the property. If `null`, the property is removed.
 */
export function updateNestedObject(obj, path, value) {
    const keys = path.split(".");
    const returnObj = { ...obj };
    let current = returnObj;

    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        // Check if the next key is not present or not an object, then create an object for it
        if (!current[key] || typeof current[key] !== "object") {
            current[key] = {};
        }
        current[key] = { ...current[key] };
        current = current[key];
    }

    const lastKey = keys[keys.length - 1];
    if (value === null) {
        // If value is null, remove the property
        delete current[lastKey];
    } else {
        // Set the value for the last key
        current[lastKey] = value;
    }

    return returnObj; // Return the updated object
}

export function getNestedObjectValue(obj, path) {
    const keys = path.split(".");
    let current = obj;

    for (let key of keys) {
        // If the key does not exist at any point, return undefined
        if (!current || !current.hasOwnProperty(key)) {
            return undefined;
        }
        current = current[key];
    }

    return current;
}

export const buildTableOptions = (tables) => {
    return tables?.map((table) => {
        const [db, ...rest] = parseFQTNC(table.fqtn);
        return {
            label: rest.join("."),
            value: table.fqtn,
        };
    });
};

export const buildColumnOptions = ({
    tables,
    disabledOptions,
    tableFilterFn,
    columnFilterFn,
}: {
    tables: Array<any>;
    disabledOptions?: Array<string>;
    columnFilterFn?: Function;
    tableFilterFn?: Function;
}) => {
    let options = [];
    const tableToConvert = tableFilterFn
        ? tables.filter((t) => tableFilterFn(t))
        : tables;
    tableToConvert?.forEach((table) => {
        const { fqtn, columns } = table;

        columns.forEach((column) => {
            if (columnFilterFn && !columnFilterFn(column)) return;
            const parsed = parseFQTNC(column.fqtc);
            const column_name = parsed.slice(-1).join(".");
            const schema_table = parsed.slice(0, 3).join(".");
            options.push({
                label: column?.tags?.includes("new")
                    ? IdHubOptionsWithTag(column_name)
                    : column_name,
                value: column.fqtc,
                category: schema_table,
                isDisabled: disabledOptions?.includes(column.fqtc),
            });
        });
    });

    return options;
};

/**
 * Extracts column names from a given data structure.
 *
 * This function takes an array of objects, each containing an `identifier_type`,
 * a `column` which can be a string, an array of strings, or an array of objects,
 * and a `comp_key`. It extracts all column names into a flat array.
 *
 * @param {Array} data - The input data array containing objects with `identifier_type`, `column`, and `comp_key`.
 * @returns {Array} - A flat array of column names.
 */
export const extractColumns = (data) => {
    const columns = [];
    data?.forEach((item) => {
        if (Array.isArray(item?.column)) {
            item?.column?.forEach((subItem) => {
                if (typeof subItem === "object") {
                    columns.push(...Object.values(subItem));
                } else {
                    columns.push(subItem);
                }
            });
        } else {
            columns.push(item.column);
        }
    });
    return columns;
};

export const areColumnsFromAllTablesSelected = (tables, columns) => {
    // Extract table names from tables array
    const tableNames = tables?.map((table) => {
        const parsed = parseFQTNC(table.fqtn);
        return parsed?.slice(0, 3).join(".");
    });

    // Extract table names from columns array
    const columnTableNames = columns?.map((column) => {
        const parsed = parseFQTNC(column);
        return parsed.slice(0, 3).join(".");
    });

    // Check if columns from all tables are present
    return (
        tableNames?.every((tableName) =>
            columnTableNames?.includes(tableName),
        ) || false
    );
};

export const mergeTablesAndEnrichedTablesObjects = (tables, enrichedTables) => {
    return tables.map((table) => {
        const enrichedTable = enrichedTables.find(
            (et) => et.fqtn === table.fqtn,
        );
        if (enrichedTable) {
            return {
                ...table,
                columns: enrichedTable.columns,
            };
        }
        return table;
    });
};

/**
 * Generates an array of enriched tables based on the provided enrichments.
 *
 * @param {Enrichment[]} enrichments - An array of enrichment objects to process.
 * @returns {Array<{ fqtn: string, columns: Array<{ fqtc: string, tags: string[] }> }>}
 * An array of enriched table objects, each containing a fully qualified table name (fqtn)
 * and an array of columns with fqtc and tags (Note: this does not include enriched columns).
 */
export const getEnrichedTables = (enrichments: Enrichment[]) => {
    const enrichedTables = [];
    enrichments?.forEach((enrichment) => {
        if (
            enrichment.id ===
                THIRD_PARTY_IDENTITY_PROVIDER.LIVERAMP_IDENTITY_SOLUTION &&
            enrichment.input_object.configuration.solution_type ===
                LIVERAMP_IDENTITY_SOLUTION_TYPE.IDENTITY_RESOLUTION
        ) {
            const columns = generateColumnsForLiveRamp({
                input_object: enrichment.input_object,
                output_object: enrichment.output_object,
            }).map((fqtc) => ({ fqtc, tags: [] }));
            enrichedTables.push({
                fqtn: enrichment.output_object.fqtn,
                columns,
            });
        }
    });
    return enrichedTables;
};

export const getEnrichedColumns = (
    fqtn: string,
    enrichments: Enrichment[],
): TableColumns[] => {
    const enrichedColumns: TableColumns[] = [];
    enrichments?.forEach((enrichment) => {
        if (enrichment.output_object.fqtn === fqtn) {
            enrichedColumns.push(
                ...enrichment.output_object.columns.filter((c) =>
                    c.tags.includes("new"),
                ),
            );
        }
    });
    return enrichedColumns;
};

export const combineColumns = (
    table: Table,
    objectColumnsMapping: Record<string, string[]>,
    enrichedColumns?: TableColumns[],
): TableColumns[] => {
    const columns =
        objectColumnsMapping[table.fqtn]?.map((c) => ({
            fqtc: `${table.fqtn}.${c}`,
            tags: [],
        })) || [];
    const enrichedCols = enrichedColumns || [];
    return [...enrichedCols, ...columns];
};

export const getCategoryDisplayNameMap = (tables) => {
    return tables?.reduce((acc, table) => {
        const parsed = parseFQTNC(table.fqtn);
        acc[table.fqtn] = parsed.slice(1, 3).join(".");
        return acc;
    }, {});
};

export const generateRelations = (
    a: Cleanroom["creator"]["allowed_join_columns"],
    b: string[],
) => {
    const result = [];

    const creatorColumnSet = a.map((c) => parseFQTNC(c)[3]);

    a.forEach((afqtc) => {
        b.forEach((fqtc) => {
            const c = parseFQTNC(fqtc)[3];
            if (creatorColumnSet.includes(c) && c === parseFQTNC(afqtc)[3]) {
                result.push({ source_column: fqtc, destination_column: afqtc });
            }
        });
    });

    return result;
};

export const getTabName = (action: string): Tab => {
    if (action === "INSTALL") {
        return "INVITED";
    } else if (action === "INSTALL_EDIT") {
        return "JOINED";
    } else {
        return "CREATED";
    }
};

/**
 * Retrieves the fully qualified table names (FQTN) of selected tables for a given application.
 *
 * @param {ApplicationTable[]} tables - The array of table applications.
 * @param {string} [tableType] - Optional table type to filter the tables.
 * @returns {string[]} An array of fully qualified table names (FQTN) that match the specified table type, or all tables if no table type is provided.
 */
export const getSelectedTablesForApplication = (
    tables: ApplicationTable[] = [],
    tableType?: string,
): string[] => {
    return tables
        .filter(
            (table: ApplicationTable) =>
                // if tableType is not provided, return all tables
                !tableType || table.table_type === tableType,
        )
        .map((table: ApplicationTable) => table.fqtn);
};

export const getPageTitle = (flow) => {
    if (flow.isProvider && !flow.isEdit) {
        return "Create Clean Room";
    } else if (!flow.isProvider && !flow.isEdit) {
        return "Install Clean Room";
    } else {
        return "Edit Clean Room";
    }
};

interface CleanroomFinishNotificationsParams {
    isCreate: boolean;
    isEdit: boolean;
    cleanroomTitle: string;
}

export const getCleanroomFinishNotifications = ({
    isCreate,
    isEdit,
    cleanroomTitle,
}: CleanroomFinishNotificationsParams): {
    title: string;
    description: string;
} => {
    if (isCreate) {
        if (!isEdit) {
            return {
                title: "Clean room creation in progress",
                description: `${cleanroomTitle} clean room is being created. This can take a few minutes.`,
            };
        } else {
            return {
                title: "Clean room update in progress",
                description: `${cleanroomTitle} clean room is being updated. This can take a few minutes.`,
            };
        }
    } else {
        if (!isEdit) {
            return {
                title: "Clean room join in progress",
                description: `${cleanroomTitle} clean room is being joined. This can take a few minutes.`,
            };
        } else {
            return {
                title: "Clean room update in progress",
                description: `${cleanroomTitle} clean room is being updated. This can take a few minutes.`,
            };
        }
    }
};

export const isPairCleanroom = (cleanroom: Cleanroom) => {
    if (cleanroom?.creator?.enrichments?.length === 0) return null;
    return cleanroom?.creator?.enrichments?.some(
        (enrichment) =>
            enrichment.id === THIRD_PARTY_IDENTITY_PROVIDER.GOOGLE_PAIR_DV360,
    );
};

export const getEnabledActivationObjectInCleanroom = ({
    isProvider,
    isEdit,
    availableActivations,
    enabledActivationsInCleanroom,
    isPairCleanroom,
}: {
    isProvider: boolean;
    isEdit: boolean;
    availableActivations: Activation[];
    enabledActivationsInCleanroom: string[];
    isPairCleanroom: boolean;
}): Activation[] => {
    const isCreateFlow = isProvider && !isEdit;
    if (isCreateFlow) {
        return availableActivations.filter((activation) => {
            if (!activation.available) return false;
            if (isPairCleanroom) return activation.id === "GOOGLE_DV_360_PAIR";
            return isPairCleanroom
                ? activation.id === "GOOGLE_DV_360_PAIR"
                : activation.id !== "GOOGLE_DV_360_PAIR";
        });
    } else {
        return availableActivations.filter((activation) =>
            enabledActivationsInCleanroom.includes(activation.id),
        );
    }
};

const supportedApplicationIdsForPrivacySettings: ApplicationId[] = [
    APPLICATION_IDS.AUDIENCE_OVERLAP_AND_SEGMENTATION,
    APPLICATION_IDS.REACH_AND_FREQUENCY,
];

export const checkShouldShowPrivacySettings = (
    selectedAppId: string | ApplicationId,
): boolean => {
    const isApplicationId = (
        id: string | ApplicationId,
    ): id is ApplicationId => {
        return Object.values(APPLICATION_IDS).includes(id as APPLICATION_IDS);
    };

    if (typeof selectedAppId === "string" && isApplicationId(selectedAppId)) {
        selectedAppId = selectedAppId as ApplicationId;
    }

    return supportedApplicationIdsForPrivacySettings.includes(
        selectedAppId as ApplicationId,
    );
};

// This function is useful for draft cleanrooms being used on the install/edit side by a consumer. It moves the collaborator
// property into the first collaborator in the list of collaborators.
const transferCollaboratorToCleanroom = (
    cleanroom: DraftCleanroom,
): Cleanroom => {
    const updatedCleanroom = cloneDeep(cleanroom);
    const { collaborator, collaborators } = cleanroom;
    // TODO: remove collaborator from cleanroom after getting clarity ftom Jason
    // delete updatedCleanroom.collaborator;

    updatedCleanroom.collaborators = collaborators?.length
        ? [{ ...collaborators[0], ...collaborator }]
        : [collaborator];

    return updatedCleanroom;
};

const removeIncompleteRelations = (cleanroom?: Cleanroom) => {
    return {
        ...cleanroom,
        collaborators: cleanroom?.collaborators.map((collaborator) => ({
            ...collaborator,
            relations: collaborator?.relations?.filter(
                (relation) =>
                    relation.source_column && relation.destination_column,
            ),
        })),
    };
};

// Sanitizes representation of a draft cleanroom object to remove incomplete data from the object.
export const sanitizeDraftCleanroom = (
    draftCleanroom: DraftCleanroom,
): DraftCleanroom => {
    const cleanroom = cloneDeep(draftCleanroom);

    return removeIncompleteRelations(cleanroom);
};

// Adds relation sources to join columns for each collaborator in a cleanroom.
// We only add join columns from relations for the collaborator and not the creator because the creator is expected to
// always have a valid list of join columns, whereas consumers retain join columns in the relations property.
function addRelationSourceToJoinColumns(cleanroom?: Cleanroom) {
    const updatedCollaborators = cleanroom.collaborators.map((collaborator) => {
        const joinColumnsSet = new Set(collaborator.allowed_join_columns);

        collaborator.relations?.forEach((relation) => {
            if (relation.source_column) {
                joinColumnsSet.add(relation.source_column);
            }
        });

        return {
            ...collaborator,
            allowed_join_columns: Array.from(joinColumnsSet),
        };
    });

    return { ...cleanroom, collaborators: updatedCollaborators };
}

// Creates a representation of a cleanroom object which is simplified for displaying in the summary card
export const createCleanroomForSummary = (
    cleanroom: Cleanroom | DraftCleanroom,
    isCreator: boolean,
): Cleanroom => {
    // Cleanroom doesn't start sanitized but becomes sanitized by the time it is returned
    let cleanroomForSummary: Cleanroom | DraftCleanroom = cloneDeep(cleanroom);

    if (isHasCollaborator(cleanroomForSummary) && !isCreator) {
        cleanroomForSummary =
            transferCollaboratorToCleanroom(cleanroomForSummary);
    }

    cleanroomForSummary = removeIncompleteRelations(cleanroomForSummary);

    cleanroomForSummary = addRelationSourceToJoinColumns(cleanroomForSummary);

    return cleanroomForSummary;
};

export const addCleanroomContextToCleanroom = (
    cleanroom: Cleanroom | DraftCleanroom,
) => {
    if (cleanroom.cleanroom_context) return cleanroom;
    return {
        ...cleanroom,
        cleanroom_context: CLEANROOM_SIDE.PROVIDER,
    };
};
